import React from "react";
import useShareLead from "@/hooks/useShareLead";
import { ActiveLinks, EmailPanel, ExpiryPicker, ShareLinkBox, WhatsAppPanel } from "@/components/leads/sharePanels";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check } from "lucide-react";

/* Email + WhatsApp sharing of a lead's property documents through expiring public links. */
export default function ShareLeadDialog({ lead, open, onClose, justCreated = false }) {
  const share = useShareLead(lead, open);
  const docs = lead?.documents || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="share-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {justCreated && <Check className="h-5 w-5 text-emerald-600" />}
            {justCreated ? "Lead created — share the documents?" : "Share property documents"}
          </DialogTitle>
          <DialogDescription>
            {lead?.customer_name} · {docs.length} document(s). Recipients open a secure link — no login needed — that expires automatically.
          </DialogDescription>
        </DialogHeader>

        {docs.length === 0 ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="share-no-docs">Attach at least one document to this lead before sharing.</div>
        ) : (
          <div className="space-y-4">
            <ExpiryPicker days={share.days} onChange={share.changeDays} expiresAt={share.link?.expires_at} />
            <ShareLinkBox url={share.link?.url} busy={share.busy} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EmailPanel busy={share.busy} result={share.emailResult} onSend={share.sendEmail} />
              <WhatsAppPanel text={share.link?.whatsapp_text} />
            </div>
            <ActiveLinks links={share.links} onRevoke={share.revoke} />
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="share-close">{justCreated ? "Skip for now" : "Close"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
